<!--
    ---------------------------------------------------
    自动播放行为
      1. 如果是 Singel Entity Page，则直接播放
      2. 如果是 Popup Entity Page，为了避免多图中视频自动播放的情况，将播放权交给 Carsousel。

    ---------------------------------------------------
    自动播放下的 Spinner 加载行为，默认显示的是封面图（poster），
      1. 如果加载速度很快，则封面图一闪而过，马上播放视频
      2. 如果加载速度比较慢，等不及自动播放，手动的点击了左上角的播放按钮，那么这个时候，封面图消失，并会出现 Spinner loading 图标
    因此总结如下，如果当前显示的是 poster，则不会显示 Spinner loading 图标。
    TODO => 说实话上面的这种体验方式并不是特别的好，

    ---------------------------------------------------
    自动播放浏览器兼容测试
      1. Chrome 没问题
      2. Safari Version 14.0 (15610.1.28.1.9, 15610) 也没有问题
-->
<template>
    <div style="width: 100%">
        <video ref="videoPlayer" class="video-js" playsinline></video>
        <video-vertical-watermark :width="width" :heigth="height" mode="center"/>
    </div>
</template>

<script>
    import videojs from 'video.js';
    import 'video.js/dist/video-js.css';
    import {isHybridAgent} from "../../utils";
    import VideoVerticalWatermark from "./VideoVerticalWatermark";
    export default {
        name: "VideoPlayer",
        components: {VideoVerticalWatermark},
        props: {
            type: {
                type: String,
                default: "video/mp4"
            },
            poster: String,
            video: String,
            // 注意，height 参数格外的重要，必须手动传入，否则视频播放将会失真
            height: Number,
            // 注意，之前就是这个 width 没有设置，导致封面显示不正确，总是向右偏斜了一半，设置于不显示封面图片了.
            width: Number,
            independent: {  // 是由自己控制自动播放还是被其它组件控制？
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                player: null,
                // 参数调节参考 https://docs.videojs.com/tutorial-options.html
                options: {
                    muted: false,
                    loop: true,
                    controls: true,
                    // 设置播放器控制器的展示项目 https://github.com/videojs/video.js/issues/953
                    // 以下四个实测有效，其它的好多无效。
                    controlBar: {
                        fullscreenToggle: false,
                        muteToggle: true,  // 注意静音按钮和 volume 按钮都是喇叭，但是是两个不同的组件，要注意了。
                        volumePanel: false,  // 让 volume 大小进度条消失；备注，这个找了好久，之前的都不行。
                        pictureInPictureToggle: false, // 没必要在播放器中展示，特别是 PC 端，移动端将来下滑离开视频后，自动的检测并画中画。
                    },
                    userActions: {
                        doubleClick: false  // 禁止双击全屏
                    },
                    // 即便是没有播放的前提下，也会自动的进行预加载，https://docs.videojs.com/tutorial-options.html#preload
                    //   + auto 表示的是加载视频和 metadata，
                    //   + metadata 表示只会加载视频的基础数据，比如视频长度等等；
                    //   + none 表示什么都不预加载。
                    // 我的场景选择 metadata 是最佳的，因为如果是多图的情况下，如果用户不喜欢一滑而过，并没有播放视频，那么之前 preload
                    // 的视频资源就浪费了；并且如果是单视频的情况下，默认就是自动播放的，因此也就不需要 preload 了。所以选择 metadata 是最佳的。
                    // preload: 'metadata',
                    // moov atom 移动到文件的头部了，依然播放慢，参考 https://stackoverflow.com/questions/13705409/mp4-in-video-js-not-playing-until-fully-loaded
                    // 的说明，将其设置为 none，感觉是起到作用了。http://www.huocool.com.cn/e/KFZ5ze5YYXf 在 H5 上基本达到瞬播了。
                    preload: 'none',
                    // https://docs.videojs.com/tutorial-options.html#autoplay 介绍必须写成 autoplay: 'muted' 才能生效！
                    // autoplay: 'play', // 只有这个属性，播放的时候会隐藏 controls panel，可以设置的参数有 muted, play
                    // autoplay: 'muted',  // muted 可以在 Safari 上直接播放，但是没有声音
                    autoplay: false, // 现在全部通过程序控制，手动控制自动播放行为。
                    height: parseFloat(this.height), // 注意，height 参数格外的重要，必须手动传入，否则视频播放将会失真
                    width: parseFloat(this.width), // width 参数也很重要，否则 poster（封面）不能正确展示
                    sources: [{
                        type: this.type,
                        src: this.video
                    }],
                    poster: this.poster
                }
            }
        },
        // 奇怪了，videojs 必须在 mounted 方法中初始化，之前换到 created() 中执行报错。
        mounted() {
            console.log('VideoPlayer mounted, route:', this.$route);
            this.player = videojs(this.$refs.videoPlayer, this.options, function onPlayerReady() {
                console.log('onPlayerReady', this);
                this.$nextTick(()=>{
                    if(this.independent){  // 避免在多图的情况下自动播放
                        this.autoplay();
                    }
                });
            }.bind(this));
            // IOS 端，取消自动全屏播放 https://stackoverflow.com/questions/50304601/videojs-disable-auto-fullscreen-after-pressing-play-button
            // 这个只在 H5 端有效，如果要在 hybrid app 中生效，必须修改 Hbuilder 工程中 manifest.json 中的 allowsInlineMediaPlayback 值
            this.player.playsinline(true);
            // 只在视频页面才允许常亮
            // TODO_ 有个想法，就是如果用户停留在这个页面，然后视频不停的循环播放，是否会影响流量，是否在 1 分钟以后，设置为 wakelock false?
            if(isHybridAgent()){
                plus.device.setWakelock(true);
                console.log('VideoPlayer mounted, wake lock on')
                // 1 分钟后关闭，避免用户长留这个页面消耗流量。
                setTimeout(()=>{
                    plus.device.setWakelock(false);
                    console.log('VideoPlayer mounted, wake lock off after 1 min')
                }, 60000);

            }
        },
        beforeDestroy() {
            console.log('video player before destroy');
            if (this.player) {
                this.$nextTick(()=>{
                    this.player.pause();
                    this.player.dispose();
                    console.log('video player get disposed');
                    // 只在播放视频的时候允许常亮
                    if(isHybridAgent()){
                        plus.device.setWakelock(false);
                        console.log('VideoPlayer beforeDestroy, wake lock off');
                    }
                });
            }
        },
        methods: {
            autoplay() {
                // 以下详细记录是如何让 video.js 支持在 Chrome 和 Safari 上自动播放的
                // https://videojs.com/blog/autoplay-best-practices-with-video-js/ autoplay 最佳实践
                // 1. 如果将 preload 设置为 metadata 即是不预加载，那么在 Safari 下，无法载启动声音的情况下，自动播放，必须使用
                //    autoplay muted；但是，如果启用这种模式，有个好处，就是 Spinner 加载行为变得正常了。
                //    ----------
                //    this.player.autoplay('muted');
                //    ----------
                // 2. 如果将 preload 开启即设置为 auto（注意，如果不配置，默认即是 auto），让它预加载视频，那么在 Safari 下，可以在
                //    启动了声音的前提下自动播放。代码如下。但是这样做的问题是，多图怎么办？多图情况下我无需预加载呀.... 或者妥协？
                //    ----------
                //    this.player.autoplay(true);
                //    ----------
                // 3. 第三种方式（即下面这种方式）才是我想要的方式，不使用预加载视频内容（preload='auto'），仅预加载 metadata 的情况下即（
                //    preload='metadata'），在 Safari 下，能够在有声音的前提下自动播放，且 Spinner 正常显示。=> 这种方式就完美了。
                //    TODO 但是 Chrome 又不干了..... 会时不时的 misfire 即无法自动播放，但 70% 的概率是可以的。解决方案参考 #4
                //    ----------
                //    this.player.autoplay('play');  // 这种方式对 Safari 友好；this.player.play() 这种方式对 Chrome 友好
                //    ----------
                // 4. 对 #3 的方案做了如下优化，发现在 Chrome 下有概率会 misfire，就是不去执行 autoplay 方法，因此试着在这里设置了
                //    一个 timeout，发现一旦设置 300ms 后，发现这种 misfire 的情况消失了。
                //    ----------
                //    setTimeout(function(){
                //        this.player.autoplay('play'); // 尝试这种方式继续播放；使用下面的方式封装，
                //    }.bind(this), 300)
                //    ----------
                // 5. 将 #4 封装为独立的方法，便于被 Carsousel 调用。
                // 6. 将 autoplay('play') 改为 autoplay('any')，官方推荐这么做，如果不能自动 unmute 播放，那么会试图 mute 自动播放。
                setTimeout(function () {
                    this.player.autoplay('any'); // 尝试这种方式继续播放；使用下面的方式封装，便于被 Carsousel 调用，
                }.bind(this), 500);
            },
            stop() {
                this.player.pause();
            }
        }
    }
</script>

<style>
</style>




// WEBPACK FOOTER //
// src/components/commons/VideoPlayer.vue